import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useFormik } from 'formik'
import { Box } from '@chakra-ui/react'
import { useDispatch } from 'react-redux'
import { getConditionType } from '@qirapagos/web/src/store/contracts/thunks'
import { AppOperation } from '@qirapagos/web/src/store/contracts/types'
import { showDrawer } from 'store/drawer/actions'
import { Drawers } from 'store/drawer/types'
import { parseDate } from 'utils/common/index'
import FormDelivery, {
  formDeliveryInitialValues,
  formDeliveryValidator,
  FormDeliveryValues
} from 'components/organisms/OperationFormSteps/FormDelivery'
import FormGrain, {
  FormGrainValues,
  formGrainValidator
} from 'components/organisms/OperationFormSteps/FormGrain'
import FormCondition, {
  formConditionInitialValues,
  formConditionValidator,
  FormConditionValues
} from 'components/organisms/OperationFormSteps/FormCondition'
import { merge } from '@qirapagos/web/src/validations/operationsForms'
import StepperContract from 'components/molecules/StepperContract'
import { Container } from './styles'

type FormValues = FormDeliveryValues &
  FormGrainValues &
  FormConditionValues & { appOperation: AppOperation }

const SalePriceFixed: React.FC = () => {
  const dispatch = useDispatch()

  const [currentStepBox, setCurrentStepBox] = useState(0)
  const [isFirstComplete, setFirstIsComplete] = useState(false)
  const [isSecondComplete, setSecondIsComplete] = useState(false)
  const [isThirdComplete, setThirdIsComplete] = useState(false)

  useEffect(() => {
    dispatch(getConditionType())
  }, [dispatch])

  const titlesInfo = [
    {
      title: '1. Condición de granos',
      isComplete: isFirstComplete
    },
    {
      title: '2. Condición de fijación',
      isComplete: isSecondComplete
    },
    {
      title: '3. Procedencia',
      isComplete: isThirdComplete
    }
  ]

  const onSubmit = (values: FormValues) => {
    const saleOrder = {
      appOperation: values.appOperation,
      // FormGrain
      sellerTaxId: values.sellerTaxId,
      currencyId: values.currencyId,
      grainId: values.grainId,
      harvestDateId: values.harvestDateId,
      price: values.price,
      tons: values.tons,
      // FormCondition
      fixationCondition: values.fixationCondition,
      conditionDateFrom: parseDate(values.conditionDateFrom, 'yyyy-MM-dd'),
      conditionDateTo: parseDate(values.conditionDateTo, 'yyyy-MM-dd'),
      // FormDelivery
      originId: values.originId,
      destinationId: values.destinationId,
      deliveryPeriod: values.deliveryPeriod,
      deliveryFrom: parseDate(values.deliveryFrom, 'yyyy-MM-dd'),
      deliveryTo: parseDate(values.deliveryTo, 'yyyy-MM-dd')
    }
    dispatch(showDrawer(Drawers.CONFIRM_NEW_SALE_ORDER, { saleOrder }))
  }

  const formik = useFormik({
    initialValues: {
      appOperation: AppOperation.SALE_TO_FIX,
      price: 0,
      currencyId: 2,
      sellerTaxId: undefined,
      grainId: undefined,
      harvestDateId: undefined,
      tons: undefined,
      ...formConditionInitialValues,
      ...formDeliveryInitialValues
    },
    validationSchema: merge(
      formGrainValidator,
      formConditionValidator,
      formDeliveryValidator
    ),
    onSubmit
  })

  useEffect(() => {
    const {
      sellerTaxId,
      currencyId,
      grainId,
      harvestDateId,
      price,
      tons,
      fixationCondition,
      conditionDateFrom,
      conditionDateTo,
      stateOrigin,
      originId,
      stateDestination,
      destinationId,
      deliveryPeriod,
      deliveryFrom,
      deliveryTo
    } = formik.values
    // FormGrain
    if (
      sellerTaxId &&
      currencyId &&
      grainId &&
      harvestDateId &&
      price &&
      tons
    ) {
      setFirstIsComplete(true)
      setCurrentStepBox(1)
    } else {
      setFirstIsComplete(false)
      setCurrentStepBox(0)
    }
    // FormCondition
    if (fixationCondition && conditionDateFrom && conditionDateTo) {
      setSecondIsComplete(true)
    } else {
      setSecondIsComplete(false)
    }
    // FormDelivery
    if (
      stateOrigin &&
      originId &&
      stateDestination &&
      destinationId &&
      deliveryPeriod &&
      deliveryFrom &&
      deliveryTo
    ) {
      setThirdIsComplete(true)
    } else {
      setThirdIsComplete(false)
    }
  }, [formik.values])

  return (
    <>
      <StepperContract
        titlesInfo={titlesInfo}
        currentStepBox={currentStepBox}
      />
      <Container>
        <Helmet title="Nueva orden de venta con precio a fijar" />
        <form onSubmit={formik.handleSubmit}>
          <Box>
            <FormGrain
              titlesInfo="1. Condición de granos"
              formik={formik}
              toFix
            />
          </Box>
          <Box>
            <FormCondition
              titlesInfo="2. Condición de fijación"
              formik={formik}
            />
          </Box>
          <Box>
            <FormDelivery titlesInfo="3. Entrega" formik={formik} />
          </Box>
        </form>
      </Container>
    </>
  )
}

export default SalePriceFixed
